import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ScSpinner from '../ScSpinner';
import {
  fetchRoleOperations,
  saveRoleOperations,
  fetchLookups,
  fetchOperations,
} from '../../studyConfigurationApi';
import {
  buildOperationTree,
  getAncestorCodes,
  getDescendantCodes,
  mergeMenuAccessItems,
} from '../../operationTreeUtils';

const ROOT_OPERATION_CODE = '-999';

const MenuAccessTreeNode = ({ node, depth, onToggle }) => {
  const isRoot = String(node.operationCode).trim() === ROOT_OPERATION_CODE;
  const rowClass = `sc-menu-tree-item${node.isFolder ? ' sc-menu-tree-folder' : ''}${isRoot ? ' sc-menu-tree-root' : ''}`;
  const label = (
    <span className="sc-menu-tree-label">
      <span className="sc-menu-tree-code">{node.operationCode}</span>
      <span className="sc-menu-tree-name">{node.operationName}</span>
    </span>
  );

  return (
  <>
    {isRoot ? (
      <div className={rowClass} style={{ '--sc-tree-depth': depth }}>
        {label}
      </div>
    ) : (
      <label className={rowClass} style={{ '--sc-tree-depth': depth }}>
        <input
          type="checkbox"
          checked={!!node.granted}
          onChange={() => onToggle(node.operationCode)}
        />
        {label}
      </label>
    )}
    {node.children?.length > 0 && (
      <div className="sc-menu-tree-children">
        {node.children.map((child) => (
          <MenuAccessTreeNode
            key={child.operationCode}
            node={child}
            depth={depth + 1}
            onToggle={onToggle}
          />
        ))}
      </div>
    )}
  </>
  );
};

const RoleAccessTab = () => {
  const [profiles, setProfiles] = useState([]);
  const [profileCode, setProfileCode] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchLookups().then((res) => {
      const list = res.data?.profiles || [];
      setProfiles(list);
      if (list.length) setProfileCode(list[0].profileCode);
    }).catch(() => setError('Failed to load profiles.'));
  }, []);

  const load = useCallback(async () => {
    if (!profileCode) return;
    setLoading(true);
    setError(null);
    try {
      const [opsRes, roleRes] = await Promise.all([
        fetchOperations(),
        fetchRoleOperations(profileCode),
      ]);
      const merged = mergeMenuAccessItems(opsRes.data, roleRes.data);
      setItems(merged);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load menu access.');
    } finally {
      setLoading(false);
    }
  }, [profileCode]);

  useEffect(() => { load(); }, [load]);

  const tree = useMemo(() => buildOperationTree(items), [items]);

  const toggle = (code) => {
    const item = items.find((i) => i.operationCode === code);
    if (!item) return;
    const nextGranted = !item.granted;
    const codesToUpdate = new Set(getDescendantCodes(items, code));
    if (nextGranted) {
      getAncestorCodes(items, code).forEach((a) => codesToUpdate.add(a));
    }
    codesToUpdate.add(code);
    setItems((prev) => prev.map((i) => (
      codesToUpdate.has(i.operationCode) ? { ...i, granted: nextGranted } : i
    )));
  };

  const save = async () => {
    setError(null);
    setSuccess(null);
    try {
      await saveRoleOperations({
        profileCode,
        operationCodes: items.filter((i) => i.granted).map((i) => i.operationCode),
      });
      setSuccess('Menu access saved.');
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed.');
    }
  };

  return (
    <div className="sc-menu-access">
      {error && <div className="sc-alert sc-alert-error">{error}</div>}
      {success && <div className="sc-alert sc-alert-success">{success}</div>}
      <div className="sc-form-row" style={{ maxWidth: 280 }}>
        <label>Profile</label>
        <select value={profileCode} onChange={(e) => setProfileCode(e.target.value)}>
          {profiles.map((p) => <option key={p.profileCode} value={p.profileCode}>{p.profileCode}</option>)}
        </select>
      </div>
      {loading ? <ScSpinner /> : (
        <>
          <div className="sc-menu-tree">
            {tree.length === 0 ? (
              <p className="sc-empty">No menu operations found.</p>
            ) : (
              tree.map((node) => (
                <MenuAccessTreeNode
                  key={node.operationCode}
                  node={node}
                  depth={0}
                  onToggle={toggle}
                />
              ))
            )}
          </div>
          <div className="sc-toolbar" style={{ marginTop: '1rem' }}>
            <button type="button" className="sc-btn sc-btn-primary" onClick={save}>Save Menu Access</button>
          </div>
        </>
      )}
    </div>
  );
};

export default RoleAccessTab;
